import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { newFetchTeacherByCourseId, createPackage, getAllPayments, triggerRetryPayment } from '../../../../../Api/apiUtils';
import { ConfigContext, PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { Button, Card, EmptyState, Field, Loader, Select, Table } from "../../../../../ui";
import { Actions, Callout, Dialog, StudentStatusBadge } from "../ui-pending";
import { toast } from "react-toastify";
import PaymentTable from "./payment-table";
import EditPackageUI from "./payment-tablev1";
import CustomPayment from './custom-payment';
import OfflineCustomPayment from './offline-custom-payment';
import { downloadInvoice, hasInvoice } from '../../../../../utils/invoiceDownload';
import "./payment-panels.scss";

const isInstrumentComboPayment = (payment: any) => {
  return payment?.instrumentCombo === true || payment?.instrumentCombo?.enabled === true;
};

const getInstrumentDiscountedPrice = (payment: any) => {
  return +(
    payment?.instrumentDiscountedPrice
    || payment?.razorpay_response?.instrumentDiscountedPrice
    || 0
  );
};

const getPaymentCourseDisplayName = (payment: any, course: any) => {
  const courseName = (course.courseName || "").trim();
  const level = (course.level || "").trim();
  // Offline course names often already end with the level (e.g. "... - Preparatory"),
  // so only append the level when it isn't already the trailing segment.
  const alreadyHasLevel = level && courseName.toLowerCase().endsWith(level.toLowerCase());
  const baseName = `${courseName}${level && !alreadyHasLevel ? ` - ${level}` : ""}${course.isFree ? " - Free" : ""}`;
  return isInstrumentComboPayment(payment) ? `${baseName} - Combo` : baseName;
};

const getPaymentCourseAmount = (payment: any, course: any, courseIndex: number) => {
  if (course.isFree) return 0;
  const courseAmount = +((course.unit_price * +course.classesToPurchase) - +(course.discountedPrice || 0)) || 0;
  const baseAmount = +(course.finalAmount || course.totalFinalPrice || 0) || courseAmount;
  if (isInstrumentComboPayment(payment) && courseIndex === 0) {
    return baseAmount + getInstrumentDiscountedPrice(payment);
  }
  return baseAmount;
};

const NewPaymentLink: React.FC<any> = ({ studentId, profileData }) => {
  const config = useContext(ConfigContext);
  const paymentConfig = useContext(PaymentConfigContext);
  const location = useLocation();
  const isOffline = new URLSearchParams(location.search).get('student') === 'offline';
  const courses = paymentConfig?.onlineCourseAcademic2 || [];
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [teachers, setTeachers] = useState<any[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [paymentModal, setPaymentModal] = useState<boolean>(false); 
  const [triggerPackageCallback, setTriggerPackageCallback] = useState<(() => void) | null>(null);
  const [paymentData, setPaymentData] = useState<any[] | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(true);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [retryLoading, setRetryLoading] = useState<{ [key: string]: boolean }>({});
  const [invoiceLoading, setInvoiceLoading] = useState<{ [key: string]: boolean }>({});
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [selectedPaymentForRetry, setSelectedPaymentForRetry] = useState<{payment: any, courseIndex: number} | null>(null);

  useEffect(() => {
    fetchPaymentDetails();
  }, [studentId]);

  useEffect(() => {
    if (!selectedCourse) return;

    const fetchTeachers = async () => {
      setLoading(true);
      setError(null);

      try {
        const courseId = selectedCourse;
        const payload = {
          page: 0,
          size: 500,
          courseId:courseId,
        };

        const response = await newFetchTeacherByCourseId(payload);
        if (response.data && Array.isArray(response.data.content)) {
          setTeachers(response.data.content);
        } else {
          setError("Unexpected API response format.");
          setTeachers([]);
        }
      } catch (error) {
        console.error("Error fetching teachers:", error);
        setError("Failed to fetch teachers. Please try again.");
        setTeachers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [selectedCourse]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await getAllPayments(studentId);
      const isPaid = (e: any) => (e.payment_status?.includes('payment_success') || e.webhook_init);
      // Offline students: show every payment-link (directpayment) order incl. freshly created
      // ones still pending payment (status "order_created"); mirrors artium-admin-portal's offline
      // payment-link list. Online keeps the paid-only filter.
      let filteredResponse = (response?.filter((e: any) =>
        e.courseList && (isOffline ? e.paymentFor === 'directpayment' : isPaid(e))
      ) || []);
      if (isOffline) {
        filteredResponse = filteredResponse.sort((a: any, b: any) => (b.created_at || 0) - (a.created_at || 0));
      }
      const processedResponse = filteredResponse.map((transaction:any) => {
        let otherDiscountAmount = 0;
          if (transaction.myCash || transaction.artiumCash || transaction.referralCode) {
            otherDiscountAmount += (+transaction.artiumCash + +transaction.myCash);
          }
          if (transaction.specialDiscount || transaction.additionalDiscount || transaction.flashDiscount) {
            otherDiscountAmount += (+transaction.specialDiscount || 0) + (+transaction.additionalDiscount || 0) + (+transaction.flashDiscount || 0);
          }

          if (otherDiscountAmount) {
            const totalPriceWithoutDiscount = transaction.courseList
              .filter((e:any) => !e.isFree)
              .reduce((acc:any, course:any) => acc + (+course.unit_price * +course.classesToPurchase), 0);
            const otherDiscountPercentage = (otherDiscountAmount / (totalPriceWithoutDiscount)) * 100;
            transaction.courseList.filter((e:any) => !e.isFree).forEach((course:any) => {
              course.discountedPrice = +course.discountedPrice || 0;
              course.discountedPrice += (+(+course.unit_price * +course.classesToPurchase) * otherDiscountPercentage / 100);
            });
          }

        return {
          ...transaction,
        };
      });
      console.log("Payment API Response:", processedResponse);
      setPaymentData(processedResponse);
    } catch (err) {
      setPaymentError("Failed to fetch payment details.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Function to format date (11-Dec-2024 format). Accepts ISO strings or epoch (s/ms).
  const formatDate = (dateInput: any) => {
    if (!dateInput && dateInput !== 0) return "N/A";
    const d = typeof dateInput === "number"
      ? new Date(dateInput < 1e12 ? dateInput * 1000 : dateInput)
      : new Date(dateInput);
    if (isNaN(d.getTime())) return "N/A";
    const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
    return d.toLocaleDateString("en-GB", options);
  };

  // Offline payment links aren't paid yet, so invoice_date is empty; fall back to the
  // order creation timestamp (mirrors artium-admin-portal which always shows createdAt).
  const getPaymentDate = (payment: any) =>
    isOffline ? (payment.createdAt ?? payment.created_at ?? payment.invoice_date) : payment.invoice_date;

  const handleDownloadInvoice = async (payment: any, key: string) => {
    setInvoiceLoading(prev => ({ ...prev, [key]: true }));
    await downloadInvoice(payment);
    setInvoiceLoading(prev => ({ ...prev, [key]: false }));
  };

  // Offline payment links are shared with the student rather than opened by the admin, so we
  // copy the link to the clipboard instead of navigating to the razorpay-hosted page.
  const copyPaymentLink = async (link: string) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Payment link copied");
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Payment link copied");
      } catch {
        toast.error("Failed to copy payment link");
      }
      document.body.removeChild(textArea);
    }
  };

  // Function to show confirmation modal for retry
  const showRetryConfirmation = (payment: any, courseIndex: number) => {
    setSelectedPaymentForRetry({ payment, courseIndex });
    setConfirmModal(true);
  };

  // Function to retry class creation for failed payments
  const retryClassCreation = async () => {
    if (!selectedPaymentForRetry) return;
    
    const { payment, courseIndex } = selectedPaymentForRetry;
    const retryKey = `${payment.id || payment.invoice_date}-${courseIndex}`;
    setRetryLoading(prev => ({ ...prev, [retryKey]: true }));
    setConfirmModal(false);

    try {
      const response = await triggerRetryPayment(payment);
      
      if (response.status === 200) {
        toast.success("Class creation retry successful!");
        fetchPaymentDetails(); // Refresh payment data
      } else {
        toast.error("Failed to retry class creation. Please try again.");
      }
    } catch (error) {
      console.error("Error retrying class creation:", error);
      toast.error("Failed to retry class creation. Please try again.");
    } finally {
      setRetryLoading(prev => ({ ...prev, [retryKey]: false }));
      setSelectedPaymentForRetry(null);
    }
  };
  

  // Handle course selection
  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourse(e.target.value);
    setSelectedTeacher("");
  };

  // Handle teacher selection
  const handleTeacherChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTeacher(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {

      console.log("selectedCourse", selectedCourse);
      console.log("teacherId", selectedTeacher);
      let teacherName = teachers.find((teacher) => teacher.teacherId === selectedTeacher)?.teacherName;
      const response = await createPackage(studentId, selectedCourse, selectedTeacher, teacherName);
      console.log("Payment Link Created:", response);
      toast.success("Payment link created successfully!");
      setModal(false);
    } catch (error) {
      setError("Failed to create payment link. Please try again.");
    }
  };


  const toggleModal = () => setModal(!modal);
  const togglePaymentModal = () => setPaymentModal(!paymentModal);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const packageCallBack = () => {
    if(triggerPackageCallback){
      triggerPackageCallback();
    }
    fetchPaymentDetails(); // Refresh payment data after package creation
    togglePaymentModal();
  }

  return (
    <div className="ds-stack payment-panel">

      <Dialog
        isOpen={modal}
        onClose={toggleModal}
        title="Create package"
        actions={
          <>
            <Button variant="secondary" onClick={toggleModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!selectedCourse || !selectedTeacher || loading}
            >
              Submit
            </Button>
          </>
        }
      >
        <Field label="Select Course">
          {(ctl) => (
            <Select
              {...ctl}
              value={selectedCourse}
              onChange={handleCourseChange}
              options={[
                { value: "", label: "Select a course" },
                ...courses.map((course: any) => ({ value: String(course.paidCourseId), label: course.name })),
              ]}
            />
          )}
        </Field>

        {selectedCourse && (
          <Field label="Select Teacher">
            {(ctl) => (
              <Select
                {...ctl}
                value={selectedTeacher}
                onChange={handleTeacherChange}
                disabled={loading}
                options={[
                  { value: "", label: "Select a teacher" },
                  ...teachers.map((teacher) => ({ value: String(teacher.teacherId), label: teacher.teacherName })),
                ]}
              />
            )}
          </Field>
        )}
        {loading && <Loader label="Loading teachers…" />}
        {error && <Callout tone="danger">{error}</Callout>}
      </Dialog>
      <Dialog size="xl" isOpen={paymentModal} onClose={togglePaymentModal} title="Create package">
        {isOffline ? (
          <OfflineCustomPayment saveCallBack={packageCallBack} profileData={profileData} studentId={studentId} />
        ) : (
          <CustomPayment saveCallBack={packageCallBack} profileData={profileData} studentId={studentId} />
        )}
      </Dialog>

      {/* Confirmation Modal for Retry */}
      <Dialog
        isOpen={confirmModal}
        onClose={toggleConfirmModal}
        title="Retry class creation?"
        actions={
          <>
            <Button variant="secondary" onClick={toggleConfirmModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={retryClassCreation}>
              Yes, Retry
            </Button>
          </>
        }
      >
        <Callout tone="warning" title="Warning">
          Please verify Unscheduled class count manually by refreshing page before proceed
        </Callout>
        <p>Are you sure you want to retry the class creation for this payment?</p>
      </Dialog>

      <Card
        title="Payment links"
        subtitle="Packages created for this student. Share a link, or customise it before it's paid."
        actions={
          <Button variant="primary" onClick={togglePaymentModal}>
            Create Package
          </Button>
        }
      >
        <PaymentTable setTriggerPackageCallback={setTriggerPackageCallback} studentId={studentId} profileData={profileData} />
      </Card>

      <Card title="Payment details" flush>
        {paymentLoading ? (
          <Loader label="Loading payment details…" />
        ) : paymentError ? (
          <Callout tone="danger">{paymentError}</Callout>
        ) : paymentData?.length ? (
          <Table caption="Payment details">
            <thead>
              <tr>
                <th>Date Created</th>
                {isOffline && <th>Class Start Date</th>}
                <th>Course Name</th>
                <th className="ds-num">No. of Classes</th>
                <th className="ds-num">Amount</th>
                <th className="ds-num">Discount</th>
                {isOffline && <th>Payment Link</th>}
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paymentData.map((payment, index) =>
                payment.courseList.map((course:any, courseIndex:any) => {
                  const retryKey = `${payment.id || payment.invoice_date}-${courseIndex}`;
                  const isRetryLoading = retryLoading[retryKey];
                  const showRetryButton = courseIndex === 0 && payment.payment_status === "payment_success_class_created_error";
                  const showInvoiceButton = courseIndex === 0 && hasInvoice(payment);
                  const isInvoiceLoading = invoiceLoading[retryKey];

                  return (
                    <tr key={`${index}-${courseIndex}`}>
                      <td className="payment-panel__nowrap">{courseIndex === 0 ? formatDate(getPaymentDate(payment)) : ""}</td>
                      {isOffline && (
                        <td className="payment-panel__nowrap">{courseIndex === 0 ? ((payment.classStartDate ?? payment.class_start_date) ? formatDate(payment.classStartDate ?? payment.class_start_date) : "-") : ""}</td>
                      )}
                      <td>{getPaymentCourseDisplayName(payment, course)}</td>
                      <td className="ds-num">{course.classesToPurchase}</td>
                      <td className="ds-num payment-panel__nowrap">{payment?.currency + " "}{getPaymentCourseAmount(payment, course, courseIndex).toFixed(2)}</td>
                      <td className="ds-num payment-panel__nowrap">{payment?.currency + " "}{(course.discountedPrice || 0).toFixed(2)}</td>
                      {isOffline && (
                        <td>
                          {courseIndex === 0 ? (() => {
                            const parts: any[] = [];
                            if (payment.payment_link) {
                              parts.push(
                                <Button
                                  key="copy"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { copyPaymentLink(payment.payment_link); }}
                                >
                                  Copy Link
                                </Button>
                              );
                            }
                            if (hasInvoice(payment)) {
                              parts.push(
                                <Button
                                  key="invoice"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { handleDownloadInvoice(payment, retryKey); }}
                                >
                                  Invoice
                                </Button>
                              );
                            }
                            if (!parts.length) return "-";
                            return <Actions align="start">{parts}</Actions>;
                          })() : ""}
                        </td>
                      )}
                      <td>
                        {courseIndex === 0 ? (
                          (isOffline && payment.payment_status === "order_created") ? (
                            <StudentStatusBadge status="created" label="New" />
                          ) : payment.payment_status ? (
                            <StudentStatusBadge status={payment.payment_status} label={payment.payment_status} />
                          ) : (
                            payment.payment_status
                          )
                        ) : ""}
                      </td>
                      <td>
                        {(showRetryButton || showInvoiceButton) && (
                          <Actions align="start" className="payment-panel__nowrap">
                            {showRetryButton && (
                              <Button
                                title="Retry to Create Classes"
                                variant="secondary"
                                size="sm"
                                onClick={() => showRetryConfirmation(payment, courseIndex)}
                                loading={isRetryLoading}
                              >
                                {isRetryLoading ? "Retrying..." : "Retry"}
                              </Button>
                            )}
                            {showInvoiceButton && (
                              <Button
                                title="Download Invoice"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadInvoice(payment, retryKey)}
                                loading={isInvoiceLoading}
                              >
                                {isInvoiceLoading ? "Downloading..." : "Download Invoice"}
                              </Button>
                            )}
                          </Actions>
                        )}
                        {courseIndex === 0 && !showInvoiceButton && !showRetryButton ? "-" : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            title="No payments yet"
            description="Payments for this student's packages will appear here."
          />
        )}
      </Card>

    </div>
  );
};

export default NewPaymentLink;